import '@adornis/authentication/saml-client/client/saml-federation-login.js';
import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/buchungsheld-components/authentication/bh-component-action-otp-login.js';
import { getUserByEmailExists } from '@adornis/buchungsheld-db/api/users.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { event } from '@adornis/chemistry/decorators/event.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@adornis/user-forms/x-login-input.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * @element ma-mentor-login
 */
@customElement('ma-mentor-login')
export class MaMentorLogin extends ChemistryLitElement {
  @event @property() mode?: 'Login_only_with_mail' | 'token_validation' = 'Login_only_with_mail';

  @state() private readonly _institutionID = new RXController(this, routingState.searchParams.get('institution'));
  @state() private readonly _translation = new TranslationController<any>(this);

  @state() private errorMessage = '';

  @state() private _email = '';

  override connectedCallback() {
    super.connectedCallback();

    if (searchParamsSignal.value.get('error_message'))
      this.errorMessage = searchParamsSignal.value.get('error_message')!;
  }
  clear() {
    this.mode = undefined;
    this._email = '';
  }

  override render() {
    if (!this.mode) throw new Error('No mode set');

    return html`
      <x-flex space="md">
        ${choose(this.mode, [
          ['Login_only_with_mail', () => this.renderLoginOnlyWithMail()],
          ['token_validation', () => this.renderValidateToken()],
        ])}
      </x-flex>
    `;
  }

  loginWithMailRenderHelper({
    withoutPassword = false,
    emailOnlyLoginCallback,
  }: {
    withoutPassword?: boolean;
    emailOnlyLoginCallback?: (email: string) => Promise<any>;
  }) {
    if (withoutPassword && !emailOnlyLoginCallback)
      throw new Error('Cannot use login render helper without password but not provide an email only login callback');

    let email = '';

    const validateEmailAndSetError = (email: string) => {
      if (!emailRegexCheck(email)) {
        logger.error({ email }, 'Did not enter valid mail address');
        this._mailError = this._translation.translate('LOGIN_PAGE', 'enterValidMailAddress');
        this.requestUpdate();
        return false;
      }
      return true;
    };

    return html`
      <x-h2> Login </x-h2>
      <x-flex space="lg" w100 crossaxis-center>
        <x-flex space="sm" ${css({ width: '250px' /* hardcoded width for x-overlay */ })}>
          <tr-login-input
            type="email"
            id="username"
            icon="mail"
            placeholder=${this._translation.translate('field', 'email')}
            @value-changed=${(e: ValueEvent<string>) => {
              if (this._email) {
                this._email = e.detail.value;
              }
              email = e.detail.value;
            }}
          ></tr-login-input>

          ${this._mailError ? html` <x-text tone="error" ${css({})}> ${this._mailError} </x-text> ` : nothing}
          ${this.errorMessage ? html` <x-text tone="error" ${css({})}> ${this.errorMessage} </x-text> ` : nothing}

          <!-- otp logic -->
          <tr-button
            icon="mail"
            @click=${() => {
              const _email = this._email || email;
              if (!validateEmailAndSetError(_email)) return;
              emailOnlyLoginCallback!(_email);
            }}
          >
            ${this._translation.translate('LOGIN_PAGE', 'loginWithoutPassword')}
          </tr-button>

          <!-- same as before; This is for users who really want to reset their password and do not understand that they could use the loginWithoutPassword button -->
          <tr-button
            mode="outline"
            icon="mail"
            @click=${() => {
              const _email = this._email || email;
              if (!validateEmailAndSetError(_email)) return;
              emailOnlyLoginCallback!(_email);
            }}
          >
            ${this._translation.translate('LOGIN_PAGE', 'forgotPassword')}
          </tr-button>

          <!-- go to registration -->
          <tr-button
            mode="outline"
            icon="mail"
            @click=${() => {
              goTo('/signup-mentor');
            }}
          >
            ${this._translation.translate('LOGIN_PAGE', 'createAccount')}
          </tr-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _mailError = '';
  renderLoginOnlyWithMail() {
    return this.loginWithMailRenderHelper({
      withoutPassword: true,
      emailOnlyLoginCallback: (email: string) => this._loginWithEmailOnly(email),
    });
  }

  renderValidateToken() {
    return html`
      <bh-component-action-otp-login
        email=${this._email}
        institution-id=${ifDefined(this._institutionID.value)}
        @login=${() => this._login()}
      ></bh-component-action-otp-login>
    `;
  }

  private async _loginWithEmailOnly(email?: string | null) {
    email = email ?? this._email;

    const userExists = await getUserByEmailExists(email)();
    if (!userExists) {
      this.errorMessage = this._translation.translate('LOGIN_PAGE', 'userDoesNotExist');
      return;
    }

    this.mode = 'token_validation';
    this._email = email;
  }

  private _login() {
    goTo('signup-mentor');
  }
}
